import {
  chronicleDeviceRecordingInfoAtom,
  chronicleReconnectedPendingEndAtom,
  chronicleSessionIdAtom,
} from '@/atoms/chronicle';
import { useAtomValue } from 'jotai';

/**
 * true when `sessionId` is the Remote recording that has reconnected
 * and is pending an end (`chronicleReconnectedPendingEndAtom`), i.e. the
 * offline→reconnect→(pre-stop) window. In this window the session must be
 * treated as STILL RECORDING — the note canvas shows the transcribing overlay,
 * never the online-segment note-gen.
 *
 * Gated purely by the existing atoms: the window is only ever armed by the
 * recovered-event listener, which fires solely when
 * `release-apps-remote-sync-async-web` is on (Rust gates `recording_recovered`
 * behind the pushed flag). Flag OFF ⇒ window never armed ⇒ this returns false.
 *
 * Two ways to match, and the second is deliberately narrow:
 *
 *  1. The window's OWN id. It is session-keyed, so this is authoritative.
 *  2. The device recording's session id — but ONLY while
 *     `chronicleSessionIdAtom` is still the window's own session. The backend
 *     id and the device recording's id can be different representations of the
 *     same recovered recording (a deferred/failed bind leaves the device id in
 *     place), and a single-id compare misses a note tab keyed on the other
 *     representation — the QA leak where the transcribing overlay dropped out
 *     and the partial online-segment note showed through. Mirrors the dual-id
 *     match in `useIsLiveOfflineRemoteSession` so the header badge,
 *     session-list row and note-canvas overlay stay in lockstep.
 *
 * The ownership qualifier on (2) is what stops a NEWER session — one that armed
 * `chronicleSessionIdAtom` while a previous stop was still draining — from
 * inheriting a reconnect window it never opened. Before the window was
 * session-keyed it was a process-global boolean, and that inheritance drove the
 * RECONNECTED card and the transcribing overlay onto the wrong recording.
 */
export function useIsRemoteReconnectedSession(
  sessionId: string | null | undefined
): boolean {
  const reconnectedSessionId = useAtomValue(chronicleReconnectedPendingEndAtom);
  const activeChronicleSessionId = useAtomValue(chronicleSessionIdAtom);
  const recordingInfo = useAtomValue(chronicleDeviceRecordingInfoAtom);

  if (reconnectedSessionId === null || !sessionId) return false;
  if (sessionId === reconnectedSessionId) return true;

  const windowStillOwnsChronicleSession =
    activeChronicleSessionId === null ||
    activeChronicleSessionId === reconnectedSessionId;
  return windowStillOwnsChronicleSession && sessionId === recordingInfo?.sessionId;
}
